import React, { useState } from 'react'
import { AccountHeader } from '@mobi/component-library/Account'
import { Button, Input } from '@mobi/component-library/Common/V2'
import { DocumentPermission } from '@mobi/account/Areas/SignUp'
import { FormLayout, DocumentBodyStyled } from '../'
import { DropdownInput } from '@mobi/component-library/Selection&Input'
import { useCountryList } from '@mobi/account/Areas/SignUp/Hooks'
import {
  ALPHANUMERIC_REGEX,
  getNameValidationMessage,
  getRequiredValidationMessage,
  isValidName,
} from '@mobi/account/Areas/SignUp/Utils'

type Props = {
  onSubmit: () => void
  isBusy: boolean
  passportNumber: string
  setPassportNumber: (value: string) => void
  birthCountry: string | null
  setBirthCountry: (value: string) => void
}

export const Passport = ({
  onSubmit,
  isBusy: isParentBusy,
  passportNumber,
  setPassportNumber,
  birthCountry,
  setBirthCountry,
}: Props) => {
  const [givePermission, setGivePermission] = useState(false)
  const [validationMessages, setValidationMessages] = useState<Record<string, string>>({})

  const countryList = useCountryList()

  const hasValidEntries = Object.values(validationMessages).every(error => error === '')
  const allowToProceed =
    hasValidEntries &&
    givePermission &&
    !!passportNumber &&
    !!birthCountry &&
    isValidName(birthCountry)

  const isBusy = countryList.isLoading || isParentBusy

  return (
    <FormLayout>
      <AccountHeader
        alignItems='start'
        title='Enter your Passport details'
        description='We need these details to be able to verify your identity'
        style={{ marginBottom: '0rem' }}
      />
      <DocumentBodyStyled>
        <Input
          name='passportNumber'
          label='Passport number'
          maxLength={15}
          placeholder='Enter passport number'
          isDisabled={isBusy}
          value={passportNumber}
          onChange={e => {
            const filteredText = e.target.value?.replace(ALPHANUMERIC_REGEX, '')
            setPassportNumber(filteredText)
          }}
          onBlur={() => {
            setValidationMessages(prev => ({
              ...prev,
              passportNumber: getRequiredValidationMessage(passportNumber),
            }))
          }}
          isInvalid={!!validationMessages.passportNumber}
          helpText={validationMessages.passportNumber}
        />
        <DropdownInput
          name='birthCountry'
          label='Issuing country'
          placeholder='Select country'
          value={birthCountry || ''}
          isDisabled={isBusy}
          onChange={event => {
            setBirthCountry(event.target.value || '')
          }}
          onBlur={() => {
            setValidationMessages(prev => ({
              ...prev,
              birthCountry: getNameValidationMessage(birthCountry),
            }))
          }}
          isInvalid={!!validationMessages.birthCountry}
          helpText={validationMessages.birthCountry}
        >
          {countryList.data?.map((country: { code: string; description: string }) => (
            <option key={country.code} value={country.code}>
              {country.description}
            </option>
          ))}
        </DropdownInput>
        <DocumentPermission isBusy={isBusy} onChange={e => setGivePermission(e.target.checked)} />
      </DocumentBodyStyled>

      <footer>
        <Button isFullWidth disabled={!allowToProceed} isLoading={isBusy} onClick={onSubmit}>
          Continue
        </Button>
      </footer>
    </FormLayout>
  )
}
